import { useLayoutEffect, useRef, useState } from "react";
import { IconCalendarCheck, IconFileDescription, IconPhone } from "@tabler/icons-react";
import { cn } from "../../design-system/components";
import { useText } from "../../clients/words";
import { podpisDaty } from "../../../chat/podpisDaty";
import { klyuchPodpisi, type HronikaPorciya, type StrokaHroniki } from "../api";
import { PodpisStroki, Puzyr } from "./Puzyr";
import { ZnachokKanala } from "./ZnachokKanala";

/**
 * ⛔⛔ ХРОНИКА КЛИЕНТКИ — ОДИН КОМПОНЕНТ НА ВСЕ ЭКРАНЫ (`s619`, слово владельца
 * 23.09: «В карточке нужна ОДНА лента по времени из всех чатов клиентки с
 * пометкой канала»).
 *
 * Её рисуют разбор обращения, мини-карточка лайта, окно переписки записи и
 * карточка клиентки — импортом, а не копией: до `s619` вторая копия в лайте
 * уже разошлась с первой (у одной не было автора, у другой — пузырей).
 *
 * Каждая строка несёт пометку канала: мессенджер И номер клиники — у сети два
 * WhatsApp. После переноса архива amo у человека бывает больше тысячи
 * сообщений за четыре года, поэтому лента листается ПОРЦИЯМИ: сверху кнопка
 * «Показать раньше», ранние строки встают над уже прочитанными, и место, где
 * человек читал, не уезжает.
 */
interface Props {
  nachalo: HronikaPorciya;
  /** Кольцо значка канала — цвет филиала экрана. */
  color: string;
  /** Чья это хроника: смена человека — ранние порции сбрасываются. */
  klyuch: number | null;
  zagruzit: (kursor: string) => Promise<HronikaPorciya>;
}

interface Rannie {
  klyuch: number | null;
  stroki: StrokaHroniki[];
  kursor: string | null;
}

const klyuchStroki = (s: StrokaHroniki, i: number) => `${s.tip}-${s.id ?? `${s.data}-${s.vremya}-${i}`}`;

export function LentaHroniki({ nachalo, color, klyuch, zagruzit }: Props) {
  const text = useText();
  const [zagruzheno, setZagruzheno] = useState<Rannie>({ klyuch, stroki: [], kursor: null });
  const [gruzim, setGruzim] = useState(false);
  const [oshibka, setOshibka] = useState(false);
  const koren = useRef<HTMLDivElement>(null);
  const prokrutka = useRef<{ el: HTMLElement; vysota: number } | null>(null);

  // Другой человек — чистый лист. Живой опрос того же человека ранние порции не трогает.
  const svoi: Rannie = zagruzheno.klyuch === klyuch ? zagruzheno : { klyuch, stroki: [], kursor: null };
  const rannie = svoi.stroki;
  // Пока ранних не грузили, курсор идёт за свежей порцией (она живая).
  const kursor = rannie.length === 0 ? (nachalo.est_esche ? nachalo.kursor : null) : svoi.kursor;

  // Ранние встали сверху — прокрутка сдвигается на их высоту, место чтения стоит.
  useLayoutEffect(() => {
    const p = prokrutka.current;
    if (p !== null) {
      p.el.scrollTop += p.el.scrollHeight - p.vysota;
      prokrutka.current = null;
    }
  }, [rannie]);

  const vidno = new Set<string>();
  const stroki: StrokaHroniki[] = [];
  for (const [i, s] of [...rannie, ...nachalo.stroki].entries()) {
    const k = klyuchStroki(s, i);
    if (s.id !== null && vidno.has(k)) continue;
    vidno.add(k);
    stroki.push(s);
  }

  const esche = kursor !== null;

  async function ranshe() {
    if (kursor === null || gruzim) return;
    setGruzim(true);
    setOshibka(false);
    try {
      const porciya = await zagruzit(kursor);
      const el = oknoProkrutki(koren.current);
      if (el !== null) prokrutka.current = { el, vysota: el.scrollHeight };
      setZagruzheno({
        klyuch,
        stroki: [...porciya.stroki, ...rannie],
        kursor: porciya.est_esche ? porciya.kursor : null,
      });
    } catch {
      setOshibka(true);
    } finally {
      setGruzim(false);
    }
  }

  if (stroki.length === 0) {
    return (
      <p className="py-8 text-center text-ops-caption text-ops-text-tertiary">
        {text("obrashcheniya.hronika_pusto")}
      </p>
    );
  }

  return (
    <div ref={koren} className="flex flex-col" data-lenta-hroniki>
      {esche && (
        <button
          type="button"
          onClick={ranshe}
          disabled={gruzim}
          className="mb-1 self-center rounded-ops-pill border border-ops-border bg-ops-surface px-3 py-1 text-ops-caption text-ops-primary hover:border-ops-primary disabled:opacity-50"
        >
          {text("obrashcheniya.hronika_ranshe", { число: nachalo.vsego })}
        </button>
      )}
      {oshibka && (
        <p className="mb-1 text-center text-ops-micro text-ops-danger">{text("obrashcheniya.hronika_ne_zagruzilas")}</p>
      )}

      {stroki.map((s, i) => {
        const novayaData = i === 0 || s.data !== stroki[i - 1].data;
        const data = novayaData && (
          <span className="my-2 self-center rounded-ops-pill border border-ops-border bg-ops-surface px-2.5 py-0.5 text-ops-micro text-ops-text-secondary">
            {podpisDaty(s.data, text)}
          </span>
        );

        if (s.tip === "soobshchenie") {
          const pred = i === 0 ? null : stroki[i - 1];
          const podpisVidna = novayaData || pred === null || pred.tip !== "soobshchenie"
            || pred.kanal_kind !== s.kanal_kind || klyuchPodpisi(pred) !== klyuchPodpisi(s);

          return (
            <div key={klyuchStroki(s, i)} className="flex flex-col">
              {data}
              {/*
                `s713`: тот же пузырь, что в «Переписках» (галочка доставки, пометка шаблона,
                причина «не ушло»). Лента идёт по всем мессенджерам клиентки — у подписи
                значок канала строки; строка без подписи (ответ прежней сборки сервера)
                называет канал по-старому.
              */}
              {s.podpis != null ? (
                podpisVidna && <PodpisStroki podpis={s.podpis} nashe={s.nashe === true} kanalKind={s.kanal_kind} color={color} />
              ) : (
                <span className={cn("mt-2 flex flex-wrap items-center gap-1 text-ops-micro text-ops-text-tertiary", s.nashe ? "self-end" : "self-start")}>
                  <ZnachokKanala kind={s.kanal_kind} color={color} razmer={14} />
                  <span>{s.kanal_kind_display}</span>
                  {s.nashe && s.avtor_imya !== null && <span>· {s.avtor_imya}</span>}
                </span>
              )}
              <Puzyr
                s={{ ...s, nashe: s.nashe === true, golos: null }}
                onRasshifrovat={() => undefined}
                zanyat={false}
                posleSvoeyPodpisi={s.podpis == null || podpisVidna}
              />
            </div>
          );
        }

        return (
          <div key={klyuchStroki(s, i)} className="flex flex-col">
            {data}
            <div className="flex items-center gap-1.5 self-center rounded-ops-pill border border-ops-border bg-ops-surface px-2.5 py-1 text-ops-micro text-ops-text-secondary">
              {s.tip === "zvonok" ? <IconPhone size={14} /> : s.tip === "zapis" ? <IconCalendarCheck size={14} /> : <IconFileDescription size={14} />}
              <span>{s.tekst}</span>
              <span className="text-ops-text-tertiary">{s.vremya}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Ближайший предок, который сам прокручивается: у каждого экрана он свой. */
function oknoProkrutki(el: HTMLElement | null): HTMLElement | null {
  let t = el?.parentElement ?? null;
  while (t !== null) {
    const oy = getComputedStyle(t).overflowY;
    if ((oy === "auto" || oy === "scroll") && t.scrollHeight > t.clientHeight) return t;
    t = t.parentElement;
  }
  return document.scrollingElement as HTMLElement | null;
}
